// A-Number / Alien Registration Number rules: the lenient pair for the employee remediation panel's OCR
// reads, and the strict Section 1 pair (`isValidSection1ANumber`, `toSection1ANumber`) for Section 1
// entry and its zod schema.
//
// Lives in `application` because the zod schema (an application concern) needs `isValidANumber`, and
// importing it from a feature's `infra/ui` adapter pointed a dependency the wrong way through the
// hexagon — and dragged UI-only code into non-UI bundles and tests. Framework-agnostic on purpose: no
// React, no DOM.

/**
 * A-Number / Alien Registration Number, per USCIS: "a unique seven-, eight- or nine-digit number that
 * the Department of Homeland Security assigned to an alien."
 *
 * The number is the digits. A leading "A" is how it is conventionally written, not part of the value,
 * so it is accepted and preserved but never required. Hyphens and spaces are formatting a person adds
 * (`A-012 345 678`) and are stripped rather than rejected.
 *
 * Used by the remediation panel, whose values come from OCR, so it strips any stray character. Section 1
 * uses the strict pair below instead, which refuses what this one quietly removes (VTK-2107).
 */
/**
 * Strip the casing and separators a person adds, without altering the number itself.
 *
 * Only a LEADING "A" is meaningful; any other letter is noise (an OCR read, or a typo).
 */
function normalizeANumber(value: string): string {
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return (cleaned.startsWith('A') ? 'A' : '') + cleaned.replace(/\D/g, '');
}

/** Input mask: normalize, then cap at nine digits so a longer number cannot be typed in the first place. */
export function formatANumber(value: string): string {
  if (!value) return '';
  const normalized = normalizeANumber(value);
  return (normalized.startsWith('A') ? 'A' : '') + normalized.replace(/\D/g, '').slice(0, 9);
}

/**
 * True when `value` IS a valid A-Number.
 *
 * Deliberately normalizes but does NOT truncate, unlike the mask. Truncating here would let a mistyped
 * ten-digit number pass validation while a *different*, silently shortened number is what gets stored —
 * and the server, which does not truncate either, would then reject it. A number that is too long is
 * invalid, not nine digits.
 */
export function isValidANumber(value: string): boolean {
  return /^A?\d{7,9}$/.test(normalizeANumber(value));
}

/**
 * The A-Number as a Section 2 or Supplement B document stores it, and as E-Verify asks for it: "A"
 * and nine digits, a shorter number zero-filled (VTK-2014). Mirrors the server's `normalizeANumber`:
 * only spaces and hyphens are dropped, and the "A" stays required, so digits alone (which could as well
 * be a USCIS number) or anything else are returned as typed for the field's own check to refuse.
 */
export function toStoredANumber(value: string): string {
  const match = /^A(\d{7,9})$/.exec(value.replace(/[\s-]/g, '').toUpperCase());
  return match ? `A${match[1].padStart(9, '0')}` : value;
}

/** The document field keys that hold an A-Number. */
export const A_NUMBER_FIELD_NAMES: readonly string[] = ['alien_number'];

/**
 * How many characters a Section 1 A-Number box takes. Room for "A-123-456-789" and the like: a box
 * cut at 10 kept "123-456-78" of "123-456-789", which normalizes to "12345678", a VALID eight-digit
 * number that is not the employee's (VTK-2107). Length is the validation's job, which refuses more
 * than nine digits.
 */
export const SECTION1_A_NUMBER_INPUT_MAX_LENGTH = 20;

/**
 * Section 1's A-Number, as the employee's attestation stores it (VTK-2107, M-274 3.0: "seven- to
 * nine-digit Alien Registration Number (A-Number) or USCIS Number", the USCIS Number being the
 * A-Number without the "A"). Spaces and hyphens are dropped and the "A" uppercased; nothing else
 * changes, so a stray letter or any other character is refused rather than quietly removed, which
 * the lenient check above does for remediation's OCR reads. Not zero-filled: E-Verify pads it.
 * The server applies the same rule to the same fields.
 */
export function toSection1ANumber(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/** True when `value` is a valid Section 1 A-Number or USCIS Number under `toSection1ANumber`. */
export function isValidSection1ANumber(value: string): boolean {
  return /^A?\d{7,9}$/.test(toSection1ANumber(value));
}
